import React, { useState, useEffect, useRef } from 'react';
import './PurchaseForm.css';
import api from '../../../services/publicApi';
import DeliveryPicker from '../DeliveryPicker/DeliveryPicker';
import { getSalesChannel, openPaymentPage } from '../../../services/telegramWebApp';

const createCheckoutKey = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `checkout-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const normalizePhone = (value) => {
    let digits = value.replace(/\D/g, '');
    if (digits.length === 10) digits = `7${digits}`;
    else if (digits.length === 11 && digits.startsWith('8')) digits = `7${digits.slice(1)}`;
    return digits.startsWith('7') && digits.length === 11 ? `+${digits}` : value.trim().replace(/[\s()-]/g, '');
};

const PurchaseForm = ({ product, variant, cartItems, initialAttempt, onAttemptChange, onBack, onCheckoutLockChange }) => {
    const initialBody = initialAttempt?.body;
    const [capabilities, setCapabilities] = useState(null);
    const [deliveryError, setDeliveryError] = useState('');
    const [deliveryRetry, setDeliveryRetry] = useState(0);
    const [deliveryQuote, setDeliveryQuote] = useState(initialAttempt?.deliveryQuote || null);
    const [shippingPaymentMode, setShippingPaymentMode] = useState(initialAttempt?.deliveryQuote?.shipping_payment_mode || 'recipient');
    const attemptRef = useRef(initialAttempt);
    const saveAttempt = value => { attemptRef.current = value; onAttemptChange?.(value); };
    useEffect(() => {
        let cancelled = false;
        setDeliveryError('');
        api.delivery.capabilities().then(value => { if (!cancelled) setCapabilities(value); })
            .catch(() => { if (!cancelled) setDeliveryError('Не удалось проверить условия доставки. Попробуйте ещё раз.'); });
        return () => { cancelled = true; };
    }, [deliveryRetry]);
    const [formData, setFormData] = useState({
        full_name: initialBody?.full_name || '',
        phone: initialBody?.phone_number || '',
        email: initialBody?.email || '',
        delivery_address: initialBody?.delivery_address || '',
        telegram: initialBody?.telegram || '',
        promocode: initialBody?.promocode || '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [promoInfo, setPromoInfo] = useState(initialAttempt?.promoInfo || null);
    const [checkingPromo, setCheckingPromo] = useState(false);
    const [agreed, setAgreed] = useState(Boolean(initialAttempt));
    const [paymentUrl, setPaymentUrl] = useState(initialAttempt?.confirmation_url || '');
    const [retryPending, setRetryPending] = useState(Boolean(initialAttempt && !initialAttempt.confirmation_url));
    const [paymentReviewRequired, setPaymentReviewRequired] = useState(false);
    const checkoutKey = useRef(initialBody?.idempotency_key || null);
    const submitting = useRef(false);
    if (!checkoutKey.current) checkoutKey.current = createCheckoutKey();

    const productName = product?.name || 'Без названия';
    const productColor = product?.color_name || '';
    const orderItems = cartItems || [{ variant_id: variant?.id, quantity: 1, price: Number(variant?.price ?? product?.price ?? 0), name: productName, size: variant?.size }];
    const productPrice = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const selectedSize = variant?.size || '';
    const fieldsDisabled = loading || retryPending || paymentReviewRequired || Boolean(paymentUrl);

    const discount = promoInfo?.valid ? (productPrice * promoInfo.discount_percent) / 100 : 0;
    const deliveryCharge = capabilities?.enabled && shippingPaymentMode === 'prepaid_fixed' ? Number(capabilities.prepaid_fixed_charge || 500) : 0;
    const finalPrice = paymentUrl && attemptRef.current?.total_amount != null ? Number(attemptRef.current.total_amount) : productPrice - discount + deliveryCharge;

    useEffect(() => {
        onCheckoutLockChange?.(loading || paymentReviewRequired || (retryPending && !paymentUrl));
        return () => onCheckoutLockChange?.(false);
    }, [loading, retryPending, paymentReviewRequired, paymentUrl, onCheckoutLockChange]);

    useEffect(() => {
        let cancelled = false;
        const code = formData.promocode.trim();
        if (initialBody && code === initialBody.promocode) return undefined;
        setPromoInfo(null);
        setCheckingPromo(Boolean(code));
        if (!code) return undefined;

        const timer = setTimeout(async () => {
            try {
                const result = await api.promo_codes.check(code);
                if (!cancelled) setPromoInfo({ ...result, checkedCode: code });
            } catch {
                if (!cancelled) setPromoInfo({ valid: false, checkedCode: code, message: 'Не удалось проверить промокод. Попробуйте снова или очистите поле.' });
            } finally {
                if (!cancelled) setCheckingPromo(false);
            }
        }, 500);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [formData.promocode, initialBody]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting.current || !agreed || paymentUrl || paymentReviewRequired) return;
        if (!retryPending && !capabilities) { setError(deliveryError || 'Дождитесь проверки условий доставки.'); return; }
        if (!retryPending && capabilities?.enabled && !deliveryQuote) { setError('Выберите город и пункт выдачи СДЭК и дождитесь расчёта доставки.'); return; }
        const promoCode = formData.promocode.trim();
        if (!retryPending && promoCode && (checkingPromo || promoInfo?.checkedCode !== promoCode)) {
            setError('Дождитесь проверки промокода.');
            return;
        }
        if (!retryPending && promoCode && !promoInfo?.valid) {
            setError(promoInfo?.message || 'Проверьте промокод или очистите поле, чтобы продолжить без скидки.');
            return;
        }
        submitting.current = true;
        setLoading(true);
        setError('');
        let requestStarted = false;

        try {
            if (!orderItems.length || orderItems.some(item => !item.variant_id)) {
                throw new Error('Выбранный размер недоступен');
            }
            const phoneNumber = normalizePhone(formData.phone);
            const email = formData.email.trim();
            if (!/^\+7\d{10}$/.test(phoneNumber) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                throw new Error('Укажите телефон в формате +7 (999) 999-99-99 или корректный email для чека.');
            }

            let body = attemptRef.current?.body || {
                ...(cartItems ? { items: orderItems.map(item => ({ variant_id: item.variant_id, quantity: item.quantity, expected_price: item.price })) }
                    : { variant_id: variant.id, expected_price: productPrice, quantity: 1 }),
                idempotency_key: checkoutKey.current,
                sales_channel: getSalesChannel(),
                phone_number: phoneNumber,
                email: email || null,
                full_name: formData.full_name.trim(),
                delivery_address: deliveryQuote?.delivery_point_address || formData.delivery_address.trim(),
                ...(deliveryQuote ? { delivery_quote_id: deliveryQuote.delivery_quote_id } : {}),
                telegram: formData.telegram.trim() || null,
            };

            if (promoInfo?.valid) {
                body.promocode = promoCode;
            }

            let checkout;
            saveAttempt({ ...attemptRef.current, body, items: orderItems, deliveryQuote, promoInfo });
            requestStarted = true;
            try {
                checkout = await api.checkout.create(body);
            } catch (err) {
                // Only a definitive expired reservation permits a new order key.
                // A network/provider failure must retry the original order.
                if (err.message !== 'checkout_expired' || err.status !== 409) throw err;
                checkoutKey.current = createCheckoutKey();
                body = { ...body, idempotency_key: checkoutKey.current };
                saveAttempt({ ...attemptRef.current, body });
                checkout = await api.checkout.create(body);
            }
            const url = new URL(checkout?.confirmation_url);
            if (url.protocol !== 'https:') throw new Error('Не удалось получить ссылку на оплату. Попробуйте ещё раз.');
            setPaymentUrl(url.href);
            setRetryPending(false);
            saveAttempt({ ...attemptRef.current, order_id: checkout.purchase_id, status_url: checkout.status_url, total_amount: checkout.total_amount, confirmation_url: url.href });
            if (getSalesChannel() !== 'telegram_mini_app') openPaymentPage(url.href);
        } catch (err) {
            const messages = {
                delivery_quote_expired: 'Расчёт доставки устарел. Выберите пункт выдачи заново.',
                delivery_quote_mismatch: 'Состав заказа изменился. Выберите пункт выдачи заново.',
                delivery_quote_required: 'Выберите пункт выдачи СДЭК и дождитесь расчёта доставки.',
                delivery_unavailable: 'Доставка временно недоступна. Попробуйте позже.',
                out_of_stock: 'Этот размер только что закончился. Вернитесь и выберите другой размер.',
                price_changed: 'Цена изменилась. Вернитесь к товару, чтобы увидеть актуальную сумму и подтвердить заказ.',
                checkout_expired: 'Время бронирования истекло. Нажмите «Перейти к оплате» ещё раз.',
                checkout_reservation_lost: 'Бронь товара больше недоступна. Вернитесь к товару и выберите размер.',
                checkout_details_changed: 'Данные этого заказа отличаются от сохранённых. Укажите исходные данные заказа или свяжитесь с магазином.',
                payment_details_rejected: 'ЮKassa не приняла данные для оплаты. Проверьте телефон и email, затем нажмите «Перейти к оплате» ещё раз.',
                payment_reconciliation_required: 'Результат предыдущей попытки оплаты требует проверки. Свяжитесь с магазином перед новым заказом, чтобы избежать повторной оплаты.',
                'Provide a valid Russian phone number or email for the receipt': 'Укажите телефон в формате +7 (999) 999-99-99 или корректный email для чека.',
                'Payment provider is temporarily unavailable': 'Не удалось связаться с ЮKassa. Попробуйте ещё раз — повторный запрос продолжит тот же заказ.',
                'Failed to fetch': 'Не удалось связаться с магазином. Проверьте интернет и попробуйте ещё раз.',
                request_timeout: 'Магазин не ответил вовремя. Нажмите «Перейти к оплате» ещё раз — продолжится тот же заказ.',
            };
            const message = messages[err.message] || err.message || 'Ошибка оформления заказа. Попробуйте ещё раз.';
            if (err.message === 'payment_details_rejected' && err.status === 422) {
                // Provider definitively rejected the intent; the backend has released it.
                // A corrected order starts only on the next explicit submit.
                checkoutKey.current = createCheckoutKey();
            }
            if (err.status && err.status < 500 && err.message !== 'payment_reconciliation_required') {
                saveAttempt(null);
                if (err.message.startsWith('delivery_quote_')) setDeliveryQuote(null);
            }
            setPaymentReviewRequired(err.message === 'payment_reconciliation_required');
            setRetryPending(requestStarted && (!err.status || err.status >= 500));
            setError(message);
        } finally {
            submitting.current = false;
            setLoading(false);
        }
    };

    return (
        <div className="shop-purchase-container">
            <button className="shop-back-btn" onClick={onBack} disabled={loading || paymentReviewRequired || (retryPending && !paymentUrl) || (Boolean(cartItems) && Boolean(paymentUrl))}>← {cartItems ? 'Назад в корзину' : 'Назад к товару'}</button>
            <h2>Оформление заказа</h2>

            <div className="shop-order-summary">
                {cartItems ? <ul className="shop-checkout-items">{orderItems.map(item => <li key={item.variant_id}>
                    <span>{item.name}{item.color ? `, ${item.color}` : ''} · {item.size} × {item.quantity}</span>
                    <strong>{new Intl.NumberFormat('ru-RU').format(item.price * item.quantity)} ₽</strong>
                </li>)}</ul> : <><p><strong>Товар:</strong> {productName}</p>
                {productColor && <p><strong>Цвет:</strong> {productColor}</p>}
                <p><strong>Размер:</strong> {selectedSize}</p></>}
                {promoInfo?.valid ? (
                    <>
                        <p className="shop-price-old">{new Intl.NumberFormat('ru-RU').format(productPrice)} ₽</p>
                        <p className="shop-price-new"><strong>Стоимость товаров со скидкой:</strong> {new Intl.NumberFormat('ru-RU').format(productPrice - discount)} ₽</p>
                        <p className="shop-promo-info">Промокод <strong>{promoInfo.code}</strong> — скидка {promoInfo.discount_percent}%</p>
                    </>
                ) : (
                    <p><strong>Стоимость товаров:</strong> {new Intl.NumberFormat('ru-RU').format(productPrice)} ₽</p>
                )}
                <p className="shop-delivery-notice">{deliveryCharge > 0 ? `Доставка СДЭК: ${deliveryCharge} ₽, включена в оплату.` : 'Доставка СДЭК оплачивается при получении отдельно от суммы заказа.'}</p>
                <p><strong>К оплате на сайте:</strong> {new Intl.NumberFormat('ru-RU').format(finalPrice)} ₽</p>
            </div>

            {error && <div className="shop-error-message" role="alert">{error}</div>}
            {paymentReviewRequired && <a href="/#contacts" target="_blank" rel="noopener noreferrer">Связаться с магазином</a>}
            {retryPending && !paymentUrl && <p className="shop-retry-notice">Данные сохранены для повторной попытки. Нажмите «Перейти к оплате» ещё раз — продолжится тот же заказ.</p>}

            {paymentUrl && (
                <div className="shop-payment-ready" role="status">
                    <p>Заказ создан. Если страница оплаты не открылась, нажмите кнопку ниже.</p>
                    {attemptRef.current?.status_url && <a className="shop-order-status-link" href={attemptRef.current.status_url}>Проверить статус заказа</a>}
                    <a className="shop-submit-btn shop-payment-link" href={paymentUrl} onClick={(event) => {
                        if (getSalesChannel() !== 'telegram_mini_app') return;
                        event.preventDefault();
                        openPaymentPage(paymentUrl);
                    }}>Открыть оплату в ЮKassa</a>
                </div>
            )}

            <form onSubmit={handleSubmit} className="shop-purchase-form">
                <div className="shop-form-group">
                    <label htmlFor="shop_full_name">ФИО *</label>
                    <input type="text" id="shop_full_name" name="full_name" value={formData.full_name} onChange={handleChange} disabled={fieldsDisabled} required maxLength={200} autoComplete="name" placeholder="Иванов Иван Иванович" />
                </div>
                <div className="shop-form-group">
                    <label htmlFor="shop_phone">Телефон *</label>
                    <input type="tel" id="shop_phone" name="phone" value={formData.phone} onChange={handleChange} disabled={fieldsDisabled} required maxLength={40} autoComplete="tel" placeholder="+7 (999) 999-99-99" />
                </div>
                <div className="shop-form-group">
                    <label htmlFor="shop_email">Email</label>
                    <input type="email" id="shop_email" name="email" value={formData.email} onChange={handleChange} disabled={fieldsDisabled} maxLength={100} autoComplete="email" placeholder="example@mail.ru" />
                </div>
                {deliveryError && !retryPending && <div role="alert">{deliveryError} <button type="button" onClick={() => setDeliveryRetry(value => value + 1)}>Повторить</button></div>}
                {!capabilities && !deliveryError && <p role="status">Проверяем условия доставки...</p>}
                {capabilities?.enabled && <fieldset className="shop-shipping-options" disabled={fieldsDisabled}>
                    <legend>Как оплатить доставку</legend>
                    <label><input type="radio" name="shipping_payment_mode" value="prepaid_fixed" checked={shippingPaymentMode === 'prepaid_fixed'} onChange={() => { setShippingPaymentMode('prepaid_fixed'); setDeliveryQuote(null); }} /><span>На сайте - {capabilities.prepaid_fixed_charge ? Number(capabilities.prepaid_fixed_charge) : 500} ₽<small>Добавим доставку к сумме заказа</small></span></label>
                    <label><input type="radio" name="shipping_payment_mode" value="recipient" checked={shippingPaymentMode === 'recipient'} onChange={() => { setShippingPaymentMode('recipient'); setDeliveryQuote(null); }} /><span>При получении в СДЭК<small>По тарифу СДЭК, отдельно от оплаты товаров</small></span></label>
                </fieldset>}
                {capabilities?.enabled || deliveryQuote ? <DeliveryPicker items={orderItems} disabled={fieldsDisabled} onChange={setDeliveryQuote} selectedQuote={deliveryQuote} shippingPaymentMode={shippingPaymentMode} /> : capabilities && <div className="shop-form-group">
                    <label htmlFor="shop_address">Укажите адрес ближайшего пункта СДЭК (доставка оплачивается отдельно от суммы заказа) *</label>
                    <textarea id="shop_address" name="delivery_address" value={formData.delivery_address} onChange={handleChange} disabled={fieldsDisabled} required maxLength={500} placeholder="Город, улица и дом, где находится пункт выдачи СДЭК" rows="3" />
                    <p className="shop-field-hint">Пункт пока выбирается вручную. Менеджер подтвердит его перед отправкой.</p>
                </div>}
                <div className="shop-form-group">
                    <label htmlFor="shop_telegram">Telegram (необязательно)</label>
                    <input
                        type="text"
                        id="shop_telegram"
                        name="telegram"
                        maxLength={100}
                        disabled={fieldsDisabled}
                        value={formData.telegram}
                        onChange={handleChange}
                        placeholder="@username, если удобнее связаться там"
                    />
                </div>
                <div className="shop-form-group">
                    <label htmlFor="shop_promocode">Промокод</label>
                    <input type="text" id="shop_promocode" name="promocode" value={formData.promocode} onChange={handleChange} disabled={fieldsDisabled} maxLength={20} placeholder="Введите промокод (если есть)" />
                    {checkingPromo && <span className="shop-promo-checking">Проверка...</span>}
                    {promoInfo && !promoInfo.valid && <span className="shop-promo-invalid">{promoInfo.message}</span>}
                </div>

               <label className="shop-agreement">
                    <input type="checkbox" checked={agreed} disabled={fieldsDisabled} onChange={(e) => setAgreed(e.target.checked)} />
                    <span>
                        Я согласен на{' '}
                        <a href="https://disk.yandex.ru/i/bPpdOnaP6_Ywkw" target="_blank" rel="noopener noreferrer">
                            обработку персональных данных
                        </a>
                    </span>
                </label>
                <button type="submit" className="shop-submit-btn" disabled={loading || !agreed || (!retryPending && (checkingPromo || !capabilities || (capabilities?.enabled && !deliveryQuote))) || paymentReviewRequired || Boolean(paymentUrl)}>
                    {loading ? 'Создание заказа...' : `Перейти к оплате (${new Intl.NumberFormat('ru-RU').format(finalPrice)} ₽)`}
                </button>
            </form>
        </div>
    );
};

export default PurchaseForm;
